import gsap from "gsap";

interface RollingNumberProps {
    target?: HTMLElement | null;
    numeric?: boolean;
    maxNumber?: number;
    duration?: number;
    startNumber?: number;
    debug?: boolean;
}

interface RollProps {
    from?: number | string;
    to?: number | string;
    duration?: number;
    callback?: (instance: _rollingNumber) => void;
}

export class _rollingNumber {
    target: HTMLElement | null;
    el: {
        main: HTMLDivElement;
        numbers: HTMLDivElement[];
    };
    numeric: boolean;
    maxNumber: number;
    duration: number;
    currentNumber: number | null;
    startNumber: number;
    height: number;
    currentOffset: number;
    debug: boolean;

    constructor(props: RollingNumberProps) {
        this.target = props.target ?? null;

        this.el = {
            main: document.createElement("div"),
            numbers: [],
        };

        this.numeric = props.numeric ?? true;
        this.maxNumber = Math.min(props.maxNumber ?? 9, 9) + 1;
        this.duration = props.duration ?? 750;
        this.currentNumber = null;
        this.startNumber = props.startNumber ?? 0;

        this.height = 0;
        this.currentOffset = 0;

        this.debug = props.debug ?? false;

        if (this.target) this.init();
    }

    init(): void {
        if (!this.target) return;

        this.target.appendChild(this.el.main);
        this.target.style.position = "relative";
        this.target.style.overflow = "hidden";

        this.el.main.style.pointerEvents = "none";
        this.el.main.style.willChange = "transform";

        for (let i = 0; i < this.maxNumber; i++) {
            this.createNumber(i);
        }
        this.createNumber(0);

        if (!this.numeric) {
            this.createNumber(",");
            this.createNumber(".");
        }

        let _resizeTimer: number;
        window.addEventListener("resize", () => {
            clearTimeout(_resizeTimer);
            _resizeTimer = window.setTimeout(() => this.resize(), 10);
        });

        this.resize("init");

        if (this.debug) console.log("_rollingNumber", this);
    }

    createNumber(value: number | string): void {
        const _number = document.createElement("div");
        const _span = document.createElement("span");
        _span.innerText = String(value);
        _number.appendChild(_span);
        this.el.numbers.push(_number);
        this.el.main.appendChild(_number);
    }

    roll(props: RollProps): void {
        let _from = typeof props?.from === "string" ? this.currentNumber ?? 0 : props?.from ?? this.currentNumber ?? 0;
        let _to = props?.to ?? 0;
        const _duration = props?.duration ?? this.duration;

        if (_to === ",") {
            _to = this.el.numbers.length - 2;
            if (_from === 0) _from = this.maxNumber;
        } else if (_to === ".") {
            _to = this.el.numbers.length - 1;
            if (_from === 0) _from = this.maxNumber;
        }

        if (this.numeric && this.currentNumber === 0 && _from === 0 && parseFloat(String(_to)) === this.maxNumber - 1) {
            _from = this.el.numbers.length - 1;
        }

        if (_from === _to) return;

        gsap.fromTo(
            this.el.main,
            {
                y: -_from * this.height,
            },
            {
                duration: _duration / 1000,
                y: -Number(_to) * this.height,
                onComplete: () => {
                    this.currentNumber = parseFloat(String(_to));
                    props.callback?.(this);
                },
            },
        );

        if (!this.numeric) {
            const fromWidth = this.el.numbers[Number(_from)]?.querySelector("span")?.offsetWidth || 0;
            const toWidth = this.el.numbers[Number(_to)]?.querySelector("span")?.offsetWidth || 0;

            gsap.fromTo(
                this.target!,
                {
                    width: fromWidth,
                },
                {
                    duration: (_duration * 0.5) / 1000,
                    delay: (_duration * 0.5) / 1000,
                    width: toWidth,
                },
            );
        }
    }

    resize(status?: "init"): void {
        let _height = 0;

        this.el.numbers.forEach((item) => {
            if (item.offsetHeight > _height) _height = item.offsetHeight;
        });

        if (_height === this.height) return;

        if (!this.numeric && this.currentNumber !== null) {
            const width = this.el.numbers[this.currentNumber]?.querySelector("span")?.offsetWidth;
            if (width) this.target!.style.width = `${width}px`;
        }

        this.height = _height;
        this.target!.style.height = `${_height}px`;

        const yFrom = -(status === "init" ? this.startNumber * this.height : this.currentOffset);
        const yTo = -(status === "init" ? this.startNumber : this.currentNumber ?? 0) * this.height;

        gsap.fromTo(
            this.el.main,
            {
                y: yFrom,
            },
            {
                duration: this.duration / 1000,
                y: yTo,
            },
        );
    }
}
